import Button from "@/components/base/Button";
import PageHero from "@/components/base/PageHero";
import ProcessFlow from "@/components/base/ProcessFlow";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import Accordion, { type FaqItem } from "@/components/base/Accordion";
import { useUi } from "@/context/UiContext";
import Seo from "@/components/base/Seo";
import { serviceSchema, faqPageSchema } from "@/lib/seo";

const TOPICS = [
  {
    icon: "ri-user-star-line",
    title: "主管與領導",
    desc: "主管角色、帶人、授權、回饋、人才發展。",
  },
  {
    icon: "ri-flag-2-line",
    title: "目標與團隊執行",
    desc: "目標對齊、角色責任、行動拆解、追蹤、協作。",
  },
  {
    icon: "ri-time-line",
    title: "自我與工作管理",
    desc: "時間、優先順序、工作規畫、問題解決、自我管理。",
  },
  {
    icon: "ri-plant-line",
    title: "人才培育與學習轉化",
    desc: "人才能力、學習目標、實作任務、課後應用、持續改善。",
  },
];

const FORMS = [
  { icon: "ri-megaphone-line", title: "專題演講" },
  { icon: "ri-group-line", title: "工作坊" },
  { icon: "ri-calendar-check-line", title: "系列培訓" },
  { icon: "ri-settings-3-line", title: "企業客製專案" },
];

const BACKGROUND = ["企業管理", "問題診斷", "流程改善", "人才培育", "教育訓練"];

const NEED_FIELDS = ["目前主要問題", "參與對象", "預計人數", "希望改變", "預計時間"];

const FAQ: FaqItem[] = [
  {
    q: "可以直接報價嗎？",
    a: "課程會依企業的實際需求、參與對象與形式規劃，因此在了解需求後才能提供較準確的規劃與報價。建議先透過需求表單或 AI 顧問與我們聊聊。",
  },
  {
    q: "可以客製嗎？",
    a: "可以。幸福人才的企業培訓以企業問題為出發點，會先理解需求與現況，再規劃適合的方案，而不是套用固定課表。",
  },
  {
    q: "可以只辦一場演講嗎？",
    a: "可以。合作形式包含專題演講、工作坊、系列培訓與企業客製專案，你可以依需求選擇適合的形式。",
  },
  {
    q: "不知道真正需要什麼課怎麼辦？",
    a: "沒關係。你不需要先有答案。可以先告訴我們目前主要問題、參與對象與希望改變的目標，我們會協助釐清需求，或先透過 AI 顧問整理想法。",
  },
];

export default function CorporateTraining() {
  const { openAi } = useUi();

  return (
    <>
      <Seo
        title="企業培訓｜主管培訓・人才培育・團隊執行力｜許文彰"
        description="幸福人才企業與團隊培訓涵蓋主管與領導、目標與團隊執行、自我與工作管理、人才培育與學習轉化四大議題，讓培訓不只發生在課堂，而是真正回到工作現場。"
        keywords="企業培訓,主管培訓,人才培育,團隊執行力,目標管理,許文彰"
        jsonLd={[serviceSchema, faqPageSchema(FAQ)]}
      />
      <PageHero
        eyebrow="企業與團隊培訓"
        title={
          <>
            讓培訓不只發生在課堂，
            <br />
            而是真正回到工作現場。
          </>
        }
        description={
          <>
            <p>
              企業真正需要的，不只是員工多聽一堂課。而是課程結束之後：
              主管能不能用不同方式帶人？團隊能不能把目標變成行動？
              人才是否開始具備更好的自我管理能力？
            </p>
          </>
        }
      >
        <Button to="/contact#corporate-form" icon="ri-arrow-right-line">
          提出企業培訓需求
        </Button>
      </PageHero>

      {/* 先理解問題 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading
              eyebrow="先理解問題"
              title="企業真正的問題，通常不是「缺一堂課」。"
            />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full rounded-card bg-cream border border-line p-7">
                <p className="text-base font-semibold text-primary">「需要時間管理課」</p>
                <p className="mt-3 text-sm text-ink-soft">背後可能是：</p>
                <ul className="mt-3 space-y-2">
                  {["優先順序混亂", "臨時任務過多", "工作規畫不足"].map((t) => (
                    <li key={t} className="flex items-center gap-2 text-sm text-ink-soft">
                      <i className="ri-arrow-right-s-line text-gold" aria-hidden="true"></i>
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="h-full rounded-card bg-cream border border-line p-7">
                <p className="text-base font-semibold text-primary">「需要主管培訓」</p>
                <p className="mt-3 text-sm text-ink-soft">背後可能是：</p>
                <ul className="mt-3 space-y-2">
                  {["新任主管不會帶人", "不會授權", "目標無法有效傳達"].map((t) => (
                    <li key={t} className="flex items-center gap-2 text-sm text-ink-soft">
                      <i className="ri-arrow-right-s-line text-gold" aria-hidden="true"></i>
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
          <Reveal delay={120}>
            <div className="mt-8 rounded-card bg-primary-dark p-8 md:p-10">
              <p className="text-lg md:text-xl font-semibold text-white leading-[1.6]">
                現在真正想改善什麼？
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 四大企業議題 */}
      <section className="bg-cream">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="四大企業議題" title="聚焦真正影響企業的關鍵議題。" />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {TOPICS.map((t, i) => (
              <Reveal key={t.title} delay={i * 80}>
                <div className="h-full rounded-card bg-white border border-line p-7 transition-transform duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 flex items-center justify-center rounded-md bg-primary/10">
                    <i className={`${t.icon} text-2xl text-primary`} aria-hidden="true"></i>
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-ink">{t.title}</h3>
                  <p className="mt-3 text-sm leading-[1.8] text-ink-soft">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 合作方式 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading
              eyebrow="合作方式"
              title="從企業問題出發，而不是從講師課表出發。"
            />
          </Reveal>
          <Reveal delay={80}>
            <div className="mt-10">
              <ProcessFlow
                steps={[
                  { title: "需求理解" },
                  { title: "問題確認" },
                  { title: "方案規畫" },
                  { title: "執行" },
                  { title: "後續" },
                ]}
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 合作形式 */}
      <section className="bg-cream">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="合作形式" title="彈性選擇適合你的形式。" />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FORMS.map((f, i) => (
              <Reveal key={f.title} delay={i * 70}>
                <div className="h-full rounded-card bg-white border border-line p-6 text-center transition-transform duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-md bg-gold-soft/70">
                    <i className={`${f.icon} text-2xl text-primary`} aria-hidden="true"></i>
                  </div>
                  <p className="mt-4 text-base font-semibold text-ink">{f.title}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 企業背景 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading
              eyebrow="企業背景"
              title="不只站在講台看問題，也從企業管理與改善的角度看問題。"
            />
            <div className="mt-8 flex flex-wrap gap-3">
              {BACKGROUND.map((b) => (
                <span
                  key={b}
                  className="inline-flex items-center gap-2 rounded-full bg-cream border border-line px-4 py-2 text-sm font-medium text-primary"
                >
                  {b}
                </span>
              ))}
            </div>
            <p className="mt-8 text-sm text-ink-soft/60 reading-width">
              正式企業／顧問經歷與照片，留待業主補資料。
            </p>
          </Reveal>
        </div>
      </section>

      {/* 企業需求入口 */}
      <section className="bg-primary-dark">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <div>
                <SectionHeading
                  eyebrow="企業需求入口"
                  title="還不知道該安排什麼課，也沒關係。"
                  dark
                />
                <p className="mt-6 text-base leading-[1.8] text-gold-soft/90">
                  先讓我們知道：
                </p>
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {NEED_FIELDS.map((n) => (
                    <li key={n} className="flex items-center gap-2 text-sm text-white/85">
                      <i className="ri-check-line text-gold" aria-hidden="true"></i>
                      {n}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="rounded-card bg-white/5 border border-white/10 p-8">
                <p className="text-lg font-semibold text-white">下一步</p>
                <div className="mt-5 flex flex-col gap-4">
                  <Button to="/contact#corporate-form" icon="ri-arrow-right-line">
                    提出企業培訓需求
                  </Button>
                  <Button onClick={openAi} variant="outline-light">
                    和 AI 顧問先整理需求
                  </Button>
                </div>
                <p className="mt-5 text-xs text-white/50">可於聯絡合作頁填寫完整需求，或先與 AI 顧問整理需求。</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="常見問題" title="關於企業培訓，你可能想知道。" />
          </Reveal>
          <Reveal delay={80}>
            <div className="mt-8 max-w-3xl">
              <Accordion items={FAQ} />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}